/* eslint-disable no-console */
/* eslint-disable camelcase */
import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  PayPalButtons,
  PayPalButtonsComponentProps,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Alert from '@mui/material/Alert';
import WebCheckoutCaptureResponsePayPalType from '../../../types/response/WebCheckoutCaptureResponsePayPalType';
import CustomerInfo from '../../../types/CustomerInfo';
import Invoice from '../../../types/Invoice';
import postWebCheckoutCreatePayPal from '../../../api/WebCheckoutCreatePayPalOrder';
import postWebCheckoutCapturePayPal from '../../../api/WebCheckoutCapturePayPalOrder';
import {
  Luci_Shopping_Api_Requests_CreatePayPalOrderRequest,
  Luci_Shopping_Api_Models_ShippingAddress,
} from '../../../types/api/v7.1';
import Styles from './PayPal.module.scss';

type PropType = {
  customerInfo: CustomerInfo;
  invoice: Invoice;
  invoiceId: string;
  deliveryMethod: string;
  onPaymentSubmit: (
    paymentToken: string,
    paypalresponse?: WebCheckoutCaptureResponsePayPalType
  ) => Promise<void>;
};

const getLatestCustomerInfo = (invoiceId: string, fallbackCustomerInfo: CustomerInfo): CustomerInfo => {
  const localStorageKey = `invoice_${invoiceId}_customerInfo`;
  try {
    const storedCustomerInfo = localStorage.getItem(localStorageKey);
    if (storedCustomerInfo) {
      const parsedInfo = JSON.parse(storedCustomerInfo);
      if (parsedInfo) {
        console.log('[PayPal] Retrieved updated customer info from local storage', parsedInfo);
        return parsedInfo;
      }
    }
  } catch (error) {
    console.error('[PayPal] Error retrieving customer info from local storage:', error);
  }
  return fallbackCustomerInfo;
};

const PayPal = ({
  customerInfo,
  invoiceId,
  deliveryMethod,
  onPaymentSubmit,
}: PropType): JSX.Element => {
  const [scriptState] = usePayPalScriptReducer();
  const { isPending, isRejected } = scriptState;
  const [open, setOpen] = useState({
    isDisplayed: false,
    message: '',
  });

  // Extract values safely
  const { email } = customerInfo;

  const handleClose = (): void => {
    setOpen({
      isDisplayed: false,
      message: open.message,
    });
  };

  const createOrder: PayPalButtonsComponentProps['createOrder'] = async () => {
    if (invoiceId) {
      // Get the latest customer info from local storage
      const latestCustomerInfo = getLatestCustomerInfo(invoiceId, customerInfo);
      
      // --- Always Construct Shipping Address if Available ---
      let shippingAddressToUse: Luci_Shopping_Api_Models_ShippingAddress | null =
        null;
      if (latestCustomerInfo.shippingAddress) {
        // Construct if latestCustomerInfo.shippingAddress exists, regardless of deliveryMethod
        shippingAddressToUse = {
          firstName: latestCustomerInfo.shippingAddress.firstName || '',
          lastName: latestCustomerInfo.shippingAddress.lastName || '',
          line1: latestCustomerInfo.shippingAddress.line1 || '',
          line2: latestCustomerInfo.shippingAddress.line2 || '',
          city: latestCustomerInfo.shippingAddress.city || '',
          state: latestCustomerInfo.shippingAddress.state || '',
          zip: latestCustomerInfo.shippingAddress.zip || '',
          country: latestCustomerInfo.shippingAddress.country || 'USA',
        };
      }

      // Map fulfillment method enum values to PayPal API delivery method enum
      // fulfillmentMethod values: 'Shipping', 'Pickup', 'PointOfSale'
      const fulfillmentToDeliveryMap: Record<
        string,
        Luci_Shopping_Api_Requests_CreatePayPalOrderRequest.deliveryMethod
      > = {
        Shipping:
          Luci_Shopping_Api_Requests_CreatePayPalOrderRequest.deliveryMethod
            .SHIPPING,
        Pickup:
          Luci_Shopping_Api_Requests_CreatePayPalOrderRequest.deliveryMethod
            .PICKUP,
        PointOfSale:
          Luci_Shopping_Api_Requests_CreatePayPalOrderRequest.deliveryMethod
            .POINT_OF_SALE,
      } as const;

      // Use the deliveryMethod prop (fulfillmentMethod enum value)
      const deliveryMethodValue:
        | Luci_Shopping_Api_Requests_CreatePayPalOrderRequest.deliveryMethod
        | undefined = deliveryMethod
        ? fulfillmentToDeliveryMap[deliveryMethod]
        : undefined;

      // Log warning if deliveryMethod doesn't map to a known value
      if (deliveryMethod && !deliveryMethodValue) {
        console.warn(
          `[PayPal] Unknown deliveryMethod value: ${deliveryMethod}. Expected: Shipping, Pickup, or PointOfSale`
        );
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('component', 'PayPal');
          scope.setTag('action', 'createOrder');
          scope.setTag('invoiceId', invoiceId);
          scope.setContext('deliveryMethod', { value: deliveryMethod });
          Sentry.captureMessage(
            `PayPal createOrder: Unknown deliveryMethod value: ${deliveryMethod}`
          );
        });
      }

      try {
        // Get the latest email from local storage if available
        const latestEmail = latestCustomerInfo.email || email;
        
        // Prepare the request body
        const body: Luci_Shopping_Api_Requests_CreatePayPalOrderRequest = {
          customerEmailAddress: latestEmail as string,
          sessionId: invoiceId,
          deliveryMethod: deliveryMethodValue,
          // billingAddress is NOT part of the CreatePayPalOrderRequest type
          // billingAddress: billingAddressToUse,
          // Conditionally include shipping address only if needed and if it exists in the type
          // API seems to require it always, so include if available
          ...(shippingAddressToUse && {
            shippingAddress: shippingAddressToUse,
          }),
        };
        
        console.log('[PayPal] Creating order with body:', body);

        // Call the backend API to create the PayPal order
        const response = await postWebCheckoutCreatePayPal(invoiceId, body);

        if (response && response.orderId) {
          return response.orderId;
        }
      } catch (error) {
        console.error('PayPal createOrder Error: API call failed', error);
        Sentry.captureException(error, {
          tags: {
            component: 'PayPal',
            action: 'createOrder',
            invoiceId,
          },
        });
        return '';
      }
    } else {
      console.error('PayPal createOrder Error: invoiceId is missing');
      Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('component', 'PayPal');
        scope.setTag('action', 'createOrder');
        Sentry.captureMessage('PayPal createOrder: invoiceId is missing');
      });
    }

    return '';
  };

  const onApprove: PayPalButtonsComponentProps['onApprove'] = async (data) => {
    // Capture the funds from the transaction.
    // Get the latest customer info from local storage
    const latestCustomerInfo = getLatestCustomerInfo(invoiceId, customerInfo);
    
    if (latestCustomerInfo) {
      try {
        const body = { payPalOrderId: data.orderID };

        const response = await postWebCheckoutCapturePayPal(body);

        // Convert the API response to the expected WebCheckoutCaptureResponsePayPalType
        const paypalResponse: WebCheckoutCaptureResponsePayPalType = {
          payer: {
            payer_id: response.payer?.payer_id || '',
            email_address: response.payer?.email_address || '',
            name: {
              given_name: response.payer?.name?.given_name || '',
              surname: response.payer?.name?.surname || '',
            },
          },
          transactionId: response.transactionId || '',
          isSuccess: response.isSuccess,
          failures: response.failures,
        };

        return onPaymentSubmit('PayPalToken', paypalResponse);
      } catch (error) {
        console.error('PayPal onApprove Error: Failed to capture payment', error);
        Sentry.captureException(error, {
          tags: {
            component: 'PayPal',
            action: 'onApprove',
            invoiceId,
            orderID: data.orderID,
          },
        });
        throw error;
      }
    } else {
      const errorMessage = 'PayPal onApprove: latestCustomerInfo is missing';
      console.error(errorMessage);
      Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('component', 'PayPal');
        scope.setTag('action', 'onApprove');
        scope.setTag('invoiceId', invoiceId);
        Sentry.captureMessage(errorMessage);
      });
    }
    return undefined;
  };

  const onCancel: PayPalButtonsComponentProps['onCancel'] = () => {
    Sentry.withScope((scope) => {
      scope.setLevel('info');
      scope.setTag('component', 'PayPal');
      scope.setTag('action', 'onCancel');
      scope.setTag('invoiceId', invoiceId);
      Sentry.captureMessage('PayPal transaction canceled by user');
    });
    setOpen({
      isDisplayed: true,
      message: 'You have canceled your PayPal transaction.',
    });
  };

  const onError: PayPalButtonsComponentProps['onError'] = (err) => {
    console.error('PayPal onError:', err);
    Sentry.captureException(err instanceof Error ? err : new Error(JSON.stringify(err)), {
      tags: {
        component: 'PayPal',
        action: 'onError',
        invoiceId,
      },
      extra: {
        errorDetails: err,
      },
    });
    setOpen({
      isDisplayed: true,
      message: 'There was an issue processing your request. Please try again.',
    });
  };

  if (isPending) {
    return (
      <div className={Styles.buttonWrapper}>
        <div className={Styles.loading}>
          <CircularProgress data-testid="loading-spinner" />
        </div>
      </div>
    );
  }

  if (isRejected) {
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setTag('component', 'PayPal');
      scope.setTag('action', 'scriptRejected');
      scope.setTag('invoiceId', invoiceId);
      Sentry.captureMessage('PayPal script loading rejected');
    });
    return (
      <div className={Styles.buttonWrapper}>
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          There was an issue with PayPal. Please try again.
        </Alert>
      </div>
    );
  }

  return (
    <div className={Styles.buttonWrapper}>
      <PayPalButtons
        createOrder={createOrder}
        onApprove={onApprove}
        onCancel={onCancel}
        onError={onError}
      />
      <Dialog
        open={open.isDisplayed}
        onClose={handleClose}
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {open.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            autoFocus
            variant="contained"
            color="primary"
          >
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PayPal;
